import { type ReactNode, useState } from 'react';

import { Icon, Tooltip } from '~/components';
import { cn } from '~/lib/cn';
import type { Direction } from '~/modules/label-perf/types';

import { Trend } from './Trend';

export interface CardProps {
  className?: string;
  contentClassName?: string;
  iconType: string;
  title: string;
  value: string | ReactNode;
  tooltipContent: string;
  trendDirection?: Direction;
  trendValue?: string;
  dataTesId?: string;
  valueTestId?: string;
}

export function Card({
  className,
  contentClassName,
  iconType,
  title,
  value,
  tooltipContent,
  trendDirection,
  trendValue,
  dataTesId,
  valueTestId,
}: CardProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const asNumber = (s: string | undefined | null) =>
    s == null
      ? NaN
      : Number.parseFloat(String(s).replace(/[^\d.-]/g, '') || 'NaN');

  const isRenderableString = typeof value === 'string';
  const mainNumeric = isRenderableString ? asNumber(value) : NaN;
  const isMainZero = Number.isFinite(mainNumeric) && mainNumeric === 0;

  const displayValue = isRenderableString && isMainZero ? '--' : value;
  const showTrend =
    !!trendDirection && Number.isFinite(mainNumeric) && mainNumeric > 0;

  return (
    <div className={cn('p-lg', className)} data-testid="card">
      <div
        className={cn('flex h-full flex-col gap-md', contentClassName)}
        data-testid="card-content"
      >
        <div className="flex gap-sm">
          <div className="flex h-[24px] w-[24px] items-center justify-center rounded-xs bg-level-05">
            <Icon type={iconType} variant="solid" size="xs" />
          </div>
          <p className="text-body-sm" data-testid={dataTesId}>
            {title}
          </p>
          {tooltipContent ? (
            <Tooltip
              content={tooltipContent}
              delayDuration={0}
              side="top"
              sideOffset={4}
              align="end"
              size="md"
              contentClassName="max-w-[300px] px-md py-sm"
              onOpenChange={setIsTooltipOpen}
            >
              <Icon
                type="info-circle"
                size="xs"
                variant={isTooltipOpen ? 'solid' : 'light'}
              />
            </Tooltip>
          ) : (
            <div>
              <Icon
                type="info-circle"
                size="xs"
                variant="light"
                className="opacity-strong"
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-sm">
          <p
            data-testid={valueTestId}
            className="text-display-5xl font-bold leading-none"
          >
            {displayValue}
          </p>
          {showTrend ? (
            <Trend direction={trendDirection}>{trendValue ?? '--'}</Trend>
          ) : null}
        </div>
      </div>
    </div>
  );
}

Card.displayName = 'Card';
